import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "tt_install_banner_dismissed";

function isStandalone() {
  return (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) || window.navigator.standalone === true;
}
function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    const onInstalled = () => { setShow(false); setIosHint(false); };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // iOS non emette beforeinstallprompt: mostra istruzioni manuali
    if (isIOS()) {
      const t = setTimeout(() => { setIosHint(true); setShow(true); }, 2500);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onBeforeInstall); window.removeEventListener("appinstalled", onInstalled); };
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch (e) { /* ignore */ }
    setDeferred(null);
    setShow(false);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 60 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          data-testid="install-banner"
          className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-6 sm:max-w-sm z-[70] border border-yellow-400/40 bg-zinc-900/95 backdrop-blur-md shadow-2xl p-4"
        >
          <button onClick={dismiss} data-testid="install-dismiss" className="absolute top-2 right-2 text-zinc-500 hover:text-zinc-200 transition-colors duration-200">
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-start gap-3 pr-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-yellow-400/15 border border-yellow-400/40">
              <Download className="h-5 w-5 text-yellow-400" />
            </span>
            <div className="min-w-0">
              <p className="font-heading text-sm font-bold uppercase tracking-wide text-zinc-50">Installa Team Tuono</p>
              {iosHint ? (
                <p className="mt-1 text-xs text-zinc-400 flex items-center gap-1 flex-wrap">
                  Tocca <Share className="inline h-3.5 w-3.5 text-cyan-400" /> Condividi, poi <strong className="text-zinc-200">"Aggiungi a Home"</strong>
                </p>
              ) : (
                <p className="mt-1 text-xs text-zinc-400">Aggiungila alla schermata Home per accedere più velocemente, anche offline.</p>
              )}
              {!iosHint && (
                <button onClick={install} data-testid="install-app-btn"
                  className="mt-3 inline-flex items-center gap-2 bg-yellow-400 text-zinc-950 font-bold text-xs tracking-[0.15em] uppercase px-4 py-2.5 hover:bg-yellow-300 transition-colors duration-200">
                  <Download className="h-4 w-4" /> Installa app
                </button>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
